import { Bounds } from '@ardoq/graph';
import {
  canvasResolveImage,
  canvasResolvedImages,
  svgImage,
} from 'tabview/canvasRendering/canvasResolvedImages';

import { ArdoqIconCategory, RepresentationData } from '@ardoq/data-model';
import { IconName, fontAwesomeIcons } from '@ardoq/icons';
import {
  AQBLOCKS_CORNERCLAMP,
  AQBLOCKS_LEAFRD_PAD,
  AQBLOCKS_SUBLABEL_BORDERCLAMP,
  AQBLOCKS_TINT,
} from '../consts';

import { BlocksNode } from '../types';
import {
  rectBloat,
  rectCenter,
  rectFit,
  rectHeight,
  rectSize,
  rectWidth,
} from 'tabview/visualizations/misc/geometry';
import { getDescendantCount, isNodeOpen } from '../util';
import { clamp } from 'lodash';
import { getComposition } from './node.composition';
import { RenderContext } from './types';
import { drawLabel, drawSublabels, drawSvgIcon } from './utils';

const detail_clip = 2;

const drawRepresentationData = (
  rd: RepresentationData,
  bg: string,
  rcIcon: Bounds,
  params: RenderContext
) => {
  const rc = params.projection.projectBounds(rcIcon);
  const { ctx } = params;
  const isFontAwesome =
    rd.icon && rd.icon.category === ArdoqIconCategory.FontAwesome;
  const isSVG = rd.icon && rd.icon.isSVG;
  const isShape = rd.shapeName;
  const globalAlpha = AQBLOCKS_TINT * ctx.globalAlpha * params.closedRdAlpha;

  const drawBackground = (outer: Bounds, cornerRadius: number) => {
    if (rectWidth(outer) > detail_clip && rectHeight(outer) > detail_clip) {
      ctx.beginPath();
      if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
        ctx.roundRect(
          outer[0],
          outer[1],
          rectWidth(outer),
          rectHeight(outer),
          cornerRadius
        );
      } else {
        ctx.rect(outer[0], outer[1], rectWidth(outer), rectHeight(outer));
      }

      ctx.fillStyle = bg;
      ctx.fill();

      // use sublabel border thickness for the rd outline

      if (params.sublabelBorderThickness > AQBLOCKS_SUBLABEL_BORDERCLAMP[0]) {
        ctx.strokeStyle = params.border(bg);
        ctx.lineWidth = params.sublabelBorderThickness;
        ctx.stroke();
      }
    }
  };

  const drawImage = (img: HTMLImageElement) => {
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    if (!imgWidth || !imgHeight) {
      // in Firefox, naturalWidth and naturalHeight will be zero if this image is an SVG with no height or width set on the root element.
      // Mozilla maintains this is not a bug. Chrome does some crazy gymnastics here, where it inspects the viewBox attribute and returns some token values which do respect the aspect ratio.
      // we're not going to do that here, because we'd have to re-fetch the image src as text and parse the viewBox out.
      // we're just going to draw the SVG as square.
      //           ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
    } else {
      const outer = rectFit(rc, [imgWidth, imgHeight]);
      const inner = rectBloat(outer, -AQBLOCKS_LEAFRD_PAD / params.pixel());
      const cornerRadius = clamp(
        0.25 * Math.min(rectWidth(inner), rectHeight(inner)),
        0,
        4
      ); // TODO from consts

      drawBackground(outer, cornerRadius);

      if (rectWidth(inner) > detail_clip && rectHeight(inner) > detail_clip) {
        ctx.save();

        if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
          // set clip on the image if there's significant corner radius
          ctx.beginPath();
          ctx.roundRect(
            inner[0],
            inner[1],
            rectWidth(inner),
            rectHeight(inner),
            cornerRadius
          );
          ctx.clip();
        }

        if (globalAlpha > 0) {
          ctx.globalAlpha = globalAlpha;
          ctx.drawImage(
            img,
            inner[0],
            inner[1],
            rectWidth(inner),
            rectHeight(inner)
          );
        }

        ctx.restore();
      }
    }
  };

  const drawIcon = () => {
    const diameter = Math.min(rectWidth(rc), rectHeight(rc));
    const outer = rectFit(rc, [diameter, diameter]);
    const cornerRadius = Math.min(4, 0.25 * diameter); // todo from consts
    const inner = rectBloat(outer, -4 / params.pixel()); // inner needs to be contained within the circle contained within outer

    drawBackground(outer, cornerRadius);

    const ctxGlobalAlpha = ctx.globalAlpha;

    if (
      globalAlpha > 0 &&
      rectWidth(inner) > detail_clip &&
      rectHeight(inner) > detail_clip
    ) {
      const fill = params.contrast(bg);

      ctx.globalAlpha = globalAlpha;

      if (isFontAwesome) {
        const { id } = rd.icon!;
        const size = diameter - 4; // this is used to get the icon font size and should be calculated from inner

        ctx.font = `${size}px FontAwesome`;
        ctx.fillStyle = fill;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(fontAwesomeIcons[id], ...rectCenter(inner));
      } else {
        const color = params.contrast(bg);
        const stroke = params.border(bg);
        const negativeColor = 'yellow';
        const key = isSVG ? rd.icon?.id : isShape ? rd.shapeName : undefined;
        const img = key
          ? svgImage(
              `${key}~~${color}~~${fill}~~${stroke}~~100%~~100%~~${negativeColor}`
            )
          : undefined;

        if (img) {
          ctx.drawImage(img, inner[0], inner[1], ...rectSize(inner));
        }
      }

      ctx.globalAlpha = ctxGlobalAlpha;
    }
  };

  if (rd && rd.isImage) {
    canvasResolveImage({ isImage: true, value: rd.value! });
    const img = canvasResolvedImages.get(rd.value!);

    if (!img?.complete) {
      return;
    }

    drawImage(img);
  } else {
    drawIcon();
  }
};

const drawDescendantCount = (
  node: BlocksNode,
  rcBadge: Bounds,
  fillColor: string,
  params: RenderContext
) => {
  const { ctx, projection } = params;
  const rc = projection.projectBoundsSnapped(rcBadge);

  ctx.font = params.sublabelFont;

  const height = rectHeight(rc);
  const cornerRadius = height / 2;
  const text =
    height > 6 && params.sublabelAlpha > 0
      ? getDescendantCount(node).toString()
      : undefined;
  const width =
    2 * cornerRadius + (text ? ctx.measureText(text).width : height);

  ctx.beginPath();

  if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
    ctx.roundRect(rc[2] - width, rc[1], width, height, cornerRadius);
  } else {
    ctx.rect(rc[2] - width, rc[1], width, height);
  }

  ctx.fillStyle = params.contrast(fillColor);
  ctx.fill();

  if (text) {
    ctx.fillStyle = params.sublabelColor(ctx.fillStyle);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, rc[2] - width + cornerRadius, 0.5 * (rc[1] + rc[3]));
  }
};

/* leaf or closed group */
export const drawClosedNode = (
  node: BlocksNode,
  bounds: Bounds,
  color: string,
  fillColor: string,
  params: RenderContext
) => {
  const { ctx, projection } = params;
  const layout = getComposition(node, bounds);

  if (
    layout.header /* && params.borderSize > ADQBLOCKS_BORDER_SIZECLAMP[0] */
  ) {
    const rc = projection.projectBoundsSnapped(layout.header);

    ctx.beginPath();
    ctx.moveTo(rc[0], rc[3]);
    ctx.lineTo(rc[2], rc[3]);
    ctx.strokeStyle = params.nodeBorder(node);
    ctx.lineWidth = params.borderSize;
    ctx.stroke();
  }

  if (layout.expander) {
    drawSvgIcon(
      isNodeOpen(node) ? IconName.UNFOLD_LESS : IconName.UNFOLD_MORE,
      layout.expander,
      fillColor,
      params
    );
  }

  if (layout.badge) {
    drawDescendantCount(node, layout.badge, fillColor, params);
  }

  if (layout.rd && node.representationData) {
    drawRepresentationData(node.representationData, color, layout.rd, params);
  }

  const { displayLabels, labelColors } = node;

  if (layout.label && displayLabels.length > 0) {
    const displayLabel = displayLabels[0];
    const labelColor = labelColors.length > 0 ? labelColors[0] : undefined;

    ctx.textAlign = 'center';
    drawLabel(displayLabel, labelColor, fillColor, layout.label, params);
  }

  if (layout.sublabels) {
    ctx.textAlign = 'center';
    drawSublabels(node, labelColors, fillColor, layout.sublabels, params);
  }
};
